import { useState, useEffect, useRef, useCallback } from 'react';

export const useDebouncedSave = (saveFunction, delay = 2000) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const timeoutRef = useRef(null);

  const debouncedSave = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => {
      try {
        setIsSaving(true);
        setHasUnsavedChanges(false);
        await saveFunction();
        setLastSaved(new Date());
      } catch (error) {
        console.error('Debounced save failed:', error);
        setHasUnsavedChanges(true);
      } finally {
        setIsSaving(false);
      }
    }, delay);
  }, [saveFunction, delay]);

  const triggerSave = useCallback(() => {
    setHasUnsavedChanges(true);
    debouncedSave();
  }, [debouncedSave]);

  // Note: The save function reference may change, but we don't reset state automatically
  // State reset is now handled explicitly by the parent component

  const resetState = useCallback(() => {
    setHasUnsavedChanges(false);
    setLastSaved(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const forceSave = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    try {
      setIsSaving(true);
      setHasUnsavedChanges(false);
      await saveFunction();
      setLastSaved(new Date());
    } catch (error) {
      console.error('Force save failed:', error);
      setHasUnsavedChanges(true);
    } finally {
      setIsSaving(false);
    }
  }, [saveFunction]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isSaving,
    lastSaved,
    hasUnsavedChanges,
    triggerSave,
    forceSave,
    resetState,
  };
};
